import React, { useState, useEffect } from 'react';
import { InputDatepicker } from '@/components/ui/inputs/input-datepicker';
import { getOverviewService, getOrdersService } from '@/services/DashboardServices';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';

export const OverviewComponent = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [dailyOrders, setDailyOrders] = useState(0);
    const [isDailyLoading, setIsDailyLoading] = useState(false);
    const today = new Date();
    const oneMonthFromToday = new Date();
    oneMonthFromToday.setMonth(today.getMonth() - 1);
    const [dateRangeSelect, setDateRangeSelect] = useState({ start_date: oneMonthFromToday, end_date: today });

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setDateRangeSelect({ start_date: start, end_date: end });
    };

    useEffect(() => {
        if (dateRangeSelect?.start_date && dateRangeSelect?.end_date) {
            getOverviewData(dateRangeSelect);
        }
    }, [dateRangeSelect?.start_date, dateRangeSelect?.end_date]);

    useEffect(() => {
        let midnightTimeout;
        let dailyInterval;

        const scheduleDailyRefresh = () => {
            const now = new Date();
            const nextMidnight = new Date(now);
            nextMidnight.setHours(24, 0, 0, 0);
            const msUntilMidnight = nextMidnight.getTime() - now.getTime();

            midnightTimeout = setTimeout(() => {
                fetchDailyOrders();
                dailyInterval = setInterval(fetchDailyOrders, 24 * 60 * 60 * 1000);
            }, msUntilMidnight);
        };

        fetchDailyOrders();
        scheduleDailyRefresh();

        return () => {
            if (midnightTimeout) clearTimeout(midnightTimeout);
            if (dailyInterval) clearInterval(dailyInterval);
        };
    }, []);

    const getOverviewData = (data) => {
        setIsLoading(true);
        dispatch(getOverviewService(data))
            .then((res) => {
                setDataSource(res?.data?.data);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const fetchDailyOrders = () => {
        const dailyDate = new Date();
        if (!dailyDate) {
            setDailyOrders(0);
            setIsDailyLoading(false);
            return;
        }
        const dailyRange = { start_date: dailyDate, end_date: dailyDate };
        setIsDailyLoading(true);
        dispatch(getOrdersService(dailyRange))
            .then((res) => {
                const customerCounts = res?.data?.data?.total_customers ?? [];
                const total = customerCounts.reduce((sum, current) => sum + Number(current || 0), 0);
                setDailyOrders(total);
                setIsDailyLoading(false);
            })
            .catch(({ message, errorData }) => {
                validationErrors(errorData, message);
                setIsDailyLoading(false);
            });
    };

    return (
        <div className="mb-9">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-[22px] leading-[34px] mb-3 capitalize">Overview</h4>
                <InputDatepicker
                    iconClassName="flex items-center me-3"
                    selected={dateRangeSelect?.start_date}
                    onChange={handleDateChange}
                    startDate={dateRangeSelect?.start_date}
                    endDate={dateRangeSelect?.end_date}
                    selectsRange
                    suffixIcon={<i className="lab lab-calendar lab-font-size-24 lab-color-pink"></i>}
                    size="sm"
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6">
                <div className="p-4 rounded-lg flex items-center gap-4 bg-[#FF4F99]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                        <i className="lab lab-total-sale lab-font-size-24 lab-color-pink"></i>
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Total Sales</h3>
                        <h4 className="font-semibold text-[22px] leading-[34px] text-white">
                            <CurrencyCodeAdd price={dataSource?.total_sales ?? 0} />
                        </h4>
                    </div>
                </div>
                <div className="p-4 rounded-lg flex items-center gap-4 bg-[#8262FE]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                        <i className="lab lab-total-orders lab-font-size-24 lab-color-portage"></i>
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Total Orders</h3>
                        <h4 className="font-semibold text-[22px] leading-[34px] text-white">{dataSource?.total_orders ?? 0}</h4>
                    </div>
                </div>
                <div className="p-4 rounded-lg flex items-center gap-4 bg-[#567DFF]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                        <i className="lab lab-total-customers lab-font-size-24 lab-color-cornflower-blue"></i>
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Total Customers</h3>
                        <h4 className="font-semibold text-[22px] leading-[34px] text-white">{dataSource?.total_customers ?? 0}+</h4>
                    </div>
                </div>
                <div className="p-4 rounded-lg flex items-center gap-4 bg-[#A953FF]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                        <i className="lab lab-total-menu-items lab-font-size-24 lab-color-heliotrope"></i>
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Total Menu Items</h3>
                        <h4 className="font-semibold text-[22px] leading-[34px] text-white">{dataSource?.total_items ?? 0}</h4>
                    </div>
                </div>
                <div className="p-4 rounded-lg flex items-center gap-4 bg-[#FFB347]">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white">
                        <i className="lab lab-total-orders lab-font-size-24 lab-color-pink"></i>
                    </div>
                    <div>
                        <h3 className="font-medium text-white">Daily Orders</h3>
                        <h4 className="font-semibold text-[22px] leading-[34px] text-white">
                            {isDailyLoading ? '...' : dailyOrders ?? 0}
                        </h4>
                    </div>
                </div>
            </div>
        </div>
    );
};
